import React, { useState, useEffect } from 'react';
import { AlertTriangle, Edit2, Check, Lock, X, ArrowLeft } from 'lucide-react';
import PropTypes from 'prop-types';
import { areResponsesDifferent, formatResponse } from '../../utils/questionUtils';
import { useNavigate } from 'react-router-dom';

const isRequired = (question) => {
    if (typeof question.required === 'boolean') {
        return question.required;
    }
    return Boolean(question.required);
};

const ReviewMode = ({ 
    questions, 
    responses,
    onEditQuestion,
    onConfirm,
    saving = false,
    organization,
    user = null
}) => {
    const [validationError, setValidationError] = useState(null);
    const [savingState, setSavingState] = useState({
        saving: false,
        error: null
    });
    const [processedResponses, setProcessedResponses] = useState({});
    const navigate = useNavigate();

    const isAdmin = user?.role === 'admin';

    // Process responses on mount and when they change
    useEffect(() => {
        const processResponses = async () => {
            const processed = {};
            for (const [questionId, response] of Object.entries(responses)) {
                // Check if this response has an organization_response field
                const hasOrgResponse = response.organization_response !== null && 
                                     response.organization_response !== undefined;

                // Process user's response
                processed[questionId] = {
                    ...response,
                    // User's own response
                    response_value: response.response_value,
                    // Organization response (if exists)
                    organization_response: hasOrgResponse ? {
                        ...response.organization_response,
                        response_value: response.organization_response.response_value
                    } : null
                };
            }
            
            console.log('Processed responses:', processed);
            setProcessedResponses(processed);
        };

        processResponses();
    }, [responses]);

    useEffect(() => {
        if (!organization?.id) {
            setSavingState({
                saving: false,
                error: 'Organization data is missing. Please try again.'
            });
        }
    }, [organization]);

    const handleConfirm = async () => {
        if (!organization?.id) {
            setSavingState({
                saving: false,
                error: 'Organization data is missing. Please try again.'
            });
            return;
        }
    
        try {
            setSavingState({ saving: true, error: null });
            setValidationError(null);
    
            await onConfirm();
            navigate('/requirements');
    
        } catch (error) {
            console.error('Error saving questionnaire:', error);
            setSavingState({
                saving: false,
                error: null
            });
            setValidationError(error.message || 'Failed to save questionnaire');
        }
    };

    const handleClose = () => {
        navigate('/requirements');
    };

    if (!organization?.id) {
        return (
            <div className="max-w-2xl mx-auto px-4 py-8">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-red-600">
                        <AlertTriangle className="w-5 h-5" />
                        <span className="text-sm font-medium">
                            Organization data is missing. Please refresh the page and try again.
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto px-4 py-8">
            <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b flex justify-between items-start">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">Review Your Responses</h2>
                        <p className="mt-1 text-sm text-gray-500">
                            Review your answers and edit them if needed
                        </p>
                        {isAdmin && (
                            <p className="mt-2 text-sm text-blue-600">
                                As an admin, your responses will be set as the organization's official answers
                            </p>
                        )}
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-gray-100 rounded-full"
                        title="Close"
                    >
                        <X className="w-5 h-5 text-gray-400" />
                    </button>
                </div>

                {validationError && (
                    <div className="px-6 py-3 bg-red-50 border-b">
                        <div className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            <span className="text-sm font-medium">{validationError}</span>
                        </div>
                    </div>
                )}

                {savingState.error && (
                    <div className="px-6 py-3 bg-red-50 border-b">
                        <div className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            <span className="text-sm font-medium">
                                {savingState.error}
                            </span>
                        </div>
                    </div>
                )}

                <div className="divide-y">
                    {questions.map((question, index) => {
                        const response = processedResponses[question.id];
                        const userResponse = response ? formatResponse(question, response) : null;
                        const orgResponse = response?.organization_response;
                        
                        const isAnswered = response !== undefined && response.response_value !== undefined;
                        const questionRequired = isRequired(question);
                        const showWarning = questionRequired && !isAnswered;
                        const hasOrgResponse = orgResponse !== null && orgResponse !== undefined;
                        
                        return (
                            <div 
                                key={`question-${question.id}-${index}`}
                                className={`px-6 py-4 hover:bg-gray-50 transition-colors ${
                                    showWarning ? 'bg-red-50' : ''
                                }`}
                                data-testid={`question-review-${index}`}
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <div className="flex items-center gap-2 flex-1">
                                                <span className="text-sm font-medium text-gray-500">
                                                    Question {index + 1} of {questions.length}
                                                </span>
                                                {questionRequired && (
                                                    <span className="text-red-500">*</span>
                                                )}
                                            </div>
                                            {showWarning && (
                                                <AlertTriangle 
                                                    className="w-5 h-5 text-red-500 flex-shrink-0" 
                                                    aria-label="Question requires an answer"
                                                />
                                            )}
                                        </div>
                                        <h3 className="mt-1 text-base font-medium text-gray-900">
                                            {question.question_text}
                                        </h3>
                                        
                                        {/* User Response Section */}
                                        <div className="mt-2">
                                            <p className="text-sm font-medium text-gray-600">Your Response:</p>
                                            <p className={`mt-1 text-sm ${
                                                isAnswered ? 'text-gray-700' : 'text-gray-400 italic'
                                            }`}>
                                                {userResponse || 'No response provided'}
                                            </p>
                                        </div>

                                        {/* Organization Response Section */}
                                        {hasOrgResponse && (
                                            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Lock className="w-4 h-4 text-blue-600" />
                                                    <span className="text-sm font-medium text-blue-800">
                                                        Organization's Official Response
                                                    </span>
                                                </div>
                                                <p className="text-sm text-blue-700">
                                                    {formatResponse(question, {
                                                        response_value: orgResponse.response_value
                                                    })}
                                                </p>
                                                {orgResponse.locked_to_user_name && (
                                                    <p className="mt-1 text-xs text-blue-600">
                                                        Set by {orgResponse.locked_to_user_name}
                                                    </p>
                                                )}
                                            </div>
                                        )}

                                        {showWarning && (
                                            <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                                                <AlertTriangle className="w-4 h-4" />
                                                This question requires an answer
                                            </p>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => onEditQuestion(index)}
                                        className="flex items-center gap-1 px-3 py-1 text-sm font-medium text-blue-600 hover:text-blue-700 rounded-md hover:bg-blue-50"
                                        data-testid={`edit-question-${index}`}
                                    >
                                        <Edit2 className="w-4 h-4" />
                                        Edit
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="px-6 py-4 border-t bg-gray-50 rounded-b-lg">
                    <div className="flex justify-between items-center">
                        <button
                            onClick={() => onEditQuestion(0)}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Edit Responses
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={savingState.saving || saving}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed"
                            data-testid="return-to-features"
                        >
                            {(savingState.saving || saving) ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                            ) : (
                                <Check className="w-4 h-4" />
                            )}
                            Return to Features
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

ReviewMode.propTypes = {
    questions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        question_text: PropTypes.string.isRequired,
        question_type: PropTypes.string.isRequired,
        required: PropTypes.oneOfType([
            PropTypes.bool,
            PropTypes.number
        ])
    })).isRequired,
    responses: PropTypes.object.isRequired,
    onEditQuestion: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    saving: PropTypes.bool,
    organization: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string
    }).isRequired,
    user: PropTypes.shape({
        id: PropTypes.string.isRequired,
        role: PropTypes.string.isRequired
    })
};

export default ReviewMode;